import React, { useContext, useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import LocationOffOutlinedIcon from '@mui/icons-material/LocationOffOutlined';
import SurfaceCard from './SurfaceCard';
import { layoutButtonPillRadius } from '../../theme/colors';
import { UserContext } from '../../contexts/UserContext';
import SnackbarContext from '../../contexts/SnackbarContext';
import deliveryWaitlistsPathTo from '../../utils/Api/deliveryWaitlistsPathTo';

const DEFAULT_RADIUS_KM = 30;

const OutOfDeliveryAreaPanel = ({
  radiusKm = DEFAULT_RADIUS_KM,
  compact = false,
  returnTo = '/home',
  showWaitlist = true,
}) => {
  const navigate = useNavigate();
  const { currentUser } = useContext(UserContext);
  const { openSnackbar } = useContext(SnackbarContext);
  const [email, setEmail] = useState(currentUser?.email || '');
  const [submitting, setSubmitting] = useState(false);

  const handleWaitlistSubmit = async (event) => {
    event.preventDefault();

    const address = currentUser?.default_address;
    if (!email.trim()) {
      openSnackbar('Informe seu e-mail para avisarmos quando chegarmos aí.', 'info');
      return;
    }

    if (!address?.cep) {
      openSnackbar('Cadastre um endereço de entrega para entrar na lista de espera.', 'info');
      return;
    }

    setSubmitting(true);
    try {
      await deliveryWaitlistsPathTo.save({
        delivery_waitlist: {
          email: email.trim(),
          cep: address.cep,
          localidade: address.localidade,
          uf: address.uf,
          latitude: address.latitude,
          longitude: address.longitude,
          user_id: currentUser?.id,
        },
      });
      openSnackbar('Pronto! Te avisaremos quando houver lojas na sua região.', 'success');
    } catch (err) {
      const msg = err?.response?.data?.errors?.[0] || 'Não foi possível cadastrar. Tente novamente.';
      openSnackbar(msg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SurfaceCard
      sx={{
        flexDirection: 'column',
        alignItems: compact ? 'flex-start' : 'center',
        textAlign: compact ? 'left' : 'center',
        gap: 2,
      }}
    >
      {!compact && (
        <LocationOffOutlinedIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignSelf: 'stretch' }}>
        <Typography variant="sectionTitle">
          Ainda não temos lojas que entregam aí
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Nunito',
            fontSize: '14px',
            lineHeight: '21px',
            color: 'text.secondary',
          }}
        >
          {`No momento atendemos em um raio de até ${radiusKm} km das nossas lojas. Tente outro endereço de entrega ou aguarde novidades na sua região.`}
        </Typography>
      </Box>

      {showWaitlist && (
        <Box
          component="form"
          onSubmit={handleWaitlistSubmit}
          sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignSelf: 'stretch' }}
        >
          <Typography
            sx={{
              fontFamily: 'Nunito',
              fontSize: '14px',
              fontWeight: 700,
              lineHeight: '14px',
              textAlign: compact ? 'left' : 'center',
            }}
          >
            Avise-me quando chegar na minha região
          </Typography>
          <TextField
            type="email"
            label="E-mail"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            size="small"
            fullWidth
            required
          />
          <Button
            type="submit"
            variant="outlined"
            disabled={submitting}
            sx={{
              alignSelf: compact ? 'flex-start' : 'center',
              borderRadius: layoutButtonPillRadius,
              textTransform: 'none',
              fontFamily: 'Nunito',
              fontWeight: 700,
            }}
          >
            {submitting ? 'Enviando…' : 'Entrar na lista de espera'}
          </Button>
        </Box>
      )}

      <Button
        variant="contained"
        onClick={() => navigate('/addresses', { state: { returnTo } })}
        sx={{
          alignSelf: compact ? 'flex-start' : 'center',
          borderRadius: layoutButtonPillRadius,
          textTransform: 'none',
          fontFamily: 'Nunito',
          fontWeight: 700,
        }}
      >
        Alterar endereço
      </Button>
    </SurfaceCard>
  );
};

export default OutOfDeliveryAreaPanel;
